import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import Button from './Button';
import { getLocations } from '../../lib/sanity';
import { trackOnlineOrderClick, trackReservationClick } from '../../utils/gtm';
import { getSanityImageProps } from '../../utils/sanityImage';

// Lazy load the location modal since it contains Mapbox
const LocationSelectionModal = lazy(() => import('./LocationSelectionModal'));

export default function MenuItemDrawer({ 
  isOpen, 
  onClose, 
  menuItem, 
  loading, 
  error,
  categorySlug,
  relatedItems
}) {
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  
  // State for location selection modal
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
  // Handle click outside to close - but only for clicks on the overlay
  useEffect(() => {
    function handleClickOutside(event) {
      // Only close if clicking directly on the drawer overlay
      if (event.target.classList.contains('drawer-overlay')) {
        // Force URL update before closing drawer
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has('item')) {
          searchParams.delete('item');
          const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
          window.history.replaceState({}, '', newUrl);
        }
        
        onClose();
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  
  // Handle escape key to close
  useEffect(() => {
    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        // Force URL update before closing drawer
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has('item')) {
          searchParams.delete('item');
          const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
          window.history.replaceState({}, '', newUrl);
        }
        
        onClose();
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);
  
  // Fetch locations when needed
  useEffect(() => {
    async function fetchLocations() {
      if (locationModalOpen && locations.length === 0) {
        setLoadingLocations(true);
        try {
          const fetchedLocations = await getLocations();
          setLocations(fetchedLocations);
        } catch (error) {
          console.error('Error fetching locations:', error);
        } finally {
          setLoadingLocations(false);
        }
      }
    }
    
    fetchLocations();
  }, [locationModalOpen, locations.length]);
  
  // Handle opening the location selection modal
  const handleOpenLocationModal = () => {
    setLocationModalOpen(true);
  };
  
  // Portal component to render at the end of the DOM
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black z-[80] drawer-overlay"
          />
          
          {/* Close button removed from here - moved inside the container */}
          
          {/* Drawer */}
          <motion.div
            ref={drawerRef}
            className="fixed inset-y-0 right-0 w-full md:w-2/3 lg:w-1/2 bg-white shadow-xl z-[90] overflow-hidden"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching the overlay
          >
            <div className="h-full flex flex-col overflow-y-auto">
              
              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto">
                {error ? (
                  <div>
                    {/* Add close button to error state */}
                    <div className="px-4 lg:px-8 pt-4 pb-0">
                      <div className="flex items-center mb-4">
                        {/* Close Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            
                            // Force URL update before closing drawer
                            const searchParams = new URLSearchParams(window.location.search);
                            if (searchParams.has('item')) {
                              searchParams.delete('item');
                              const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
                              window.history.replaceState({}, '', newUrl);
                            }
                            
                            onClose();
                          }}
                          className="mr-3 bg-brand-navy rounded-full p-1.5 text-white hover:bg-opacity-90 transition-colors flex-shrink-0"
                          aria-label="Close menu item details"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-8 text-center">
                      <h2 className="text-2xl font-heading text-brand-navy mb-4">Menu Item Not Found</h2>
                      <p className="text-gray-700 mb-6">{error || "We couldn't find the menu item you're looking for."}</p>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        className="text-brand-navy underline"
                      >
                        Return to Menu
                      </button>
                    </div>
                  </div>
                ) : loading || !menuItem ? (
                  <div className="animate-pulse">
                    {/* Breadcrumb Skeleton with Close Button */}
                    <div className="px-4 lg:px-8 pt-4 pb-0">
                      <div className="flex items-center mb-4">
                        {/* Close Button Skeleton */}
                        <div className="mr-3 w-8 h-8 bg-gray-300 rounded-full flex-shrink-0"></div>
                        
                        {/* Breadcrumb skeleton wrapper */}
                        <div className="flex flex-wrap items-center gap-y-1 min-w-0 flex-1">
                          <div className="h-4 w-16 bg-gray-200 rounded"></div>
                          <div className="mx-2 h-3 w-3 bg-gray-200 rounded flex-shrink-0"></div>
                          <div className="h-4 w-24 bg-gray-200 rounded"></div>
                          <div className="mx-2 h-3 w-3 bg-gray-200 rounded flex-shrink-0"></div>
                          <div className="h-4 w-32 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Desktop: Side-by-side layout | Mobile: Stacked layout */}
                    <div className="px-4 lg:px-8 pt-2 pb-0 flex flex-col lg:flex-row lg:gap-8">
                      {/* Image Skeleton - 1:1 aspect ratio on left */}
                      <div className="lg:w-1/2 xl:w-2/5 mb-6 lg:mb-0">
                        <div className="w-full aspect-square bg-gray-200 rounded-xl"></div>
                      </div>
                      
                      {/* Content Skeleton on right - bottom aligned */}
                      <div className="lg:w-1/2 xl:w-3/5 lg:flex lg:flex-col lg:justify-end">
                        {/* Title and Calories Skeleton */}
                        <div className="mb-2">
                          <div className="h-8 w-48 bg-gray-200 rounded mb-1"></div>
                          <div className="h-4 w-16 bg-gray-200 rounded"></div>
                        </div>
                        
                        {/* Description Skeleton */}
                        <div className="space-y-2 mb-6">
                          <div className="h-4 w-full bg-gray-200 rounded"></div>
                          <div className="h-4 w-5/6 bg-gray-200 rounded"></div>
                          <div className="h-4 w-4/6 bg-gray-200 rounded"></div>
                        </div>
                        
                        {/* Buttons Skeleton */}
                        <div className="flex flex-wrap gap-3 mb-6">
                          <div className="h-10 w-36 bg-gray-200 rounded"></div>
                          <div className="h-10 w-36 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Locations Skeleton */}
                    <div className="mt-8 bg-gray-50 pt-6 pb-6">
                      <div className="px-4 lg:px-8">
                        <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                          <div className="h-12 bg-white rounded-md"></div>
                          <div className="h-12 bg-white rounded-md"></div>
                          <div className="h-12 bg-white rounded-md"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Related Items Skeleton */}
                    <div className="-mt-px -mb-4 bg-brand-navy py-6">
                      <div className="px-4 lg:px-8">
                        <div className="h-6 w-48 bg-gray-600 rounded mb-4"></div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg overflow-hidden">
                          <div className="h-32 bg-gray-300"></div>
                          <div className="p-4">
                            <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                            <div className="h-4 w-full bg-gray-200 rounded"></div>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg overflow-hidden">
                          <div className="h-32 bg-gray-300"></div>
                          <div className="p-4">
                            <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                            <div className="h-4 w-full bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                    {/* Breadcrumb Navigation with Close Button */}
                    <div className="px-4 lg:px-8 pt-4 pb-0">
                      <div className="flex items-center mb-4">
                        {/* Close Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            
                            // Force URL update before closing drawer
                            const searchParams = new URLSearchParams(window.location.search);
                            if (searchParams.has('item')) {
                              searchParams.delete('item');
                              const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
                              window.history.replaceState({}, '', newUrl);
                            }
                            
                            onClose();
                          }}
                          className="mr-3 bg-brand-navy rounded-full p-1.5 text-white hover:bg-opacity-90 transition-colors flex-shrink-0"
                          aria-label="Close menu item details"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        
                        {/* Breadcrumb wrapper */}
                        <div className="flex flex-wrap items-center text-sm font-medium gap-y-1 min-w-0 flex-1">
                          <Link 
                            to="/menu" 
                            className="text-brand-navy hover:text-brand-orange transition-colors whitespace-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              
                              // Force URL update before closing drawer
                              const searchParams = new URLSearchParams(window.location.search);
                              if (searchParams.has('item')) {
                                searchParams.delete('item');
                                const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
                                window.history.replaceState({}, '', newUrl);
                              }
                              
                              onClose();
                            }}
                          >
                            Menu
                          </Link>
                          
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          
                          <Link 
                            to={`/menu/${categorySlug}`} 
                            className="text-brand-navy hover:text-brand-orange transition-colors whitespace-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              
                              // Force URL update before closing drawer
                              const searchParams = new URLSearchParams(window.location.search);
                              if (searchParams.has('item')) {
                                searchParams.delete('item');
                                const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
                                window.history.replaceState({}, '', newUrl);
                              }
                              
                              onClose();
                            }}
                          >
                            {menuItem.category && menuItem.category.name ? menuItem.category.name : 'Category'}
                          </Link>
                          
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          
                          <span className="text-gray-500 min-w-0">
                            {menuItem.name}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Desktop: Side-by-side layout | Mobile: Stacked layout */}
                    <div className="px-4 lg:px-8 pt-2 pb-0 flex flex-col lg:flex-row lg:gap-8">
                      {/* Image with 1:1 aspect ratio on left */}
                      <div className="lg:w-1/2 xl:w-2/5 mb-6 lg:mb-0">
                        <div className="relative w-full aspect-square">
                          {menuItem.imageUrl ? (
                            <img 
                              {...getSanityImageProps(menuItem.imageUrl, 'menuDetail')}
                              alt={menuItem.name} 
                              className="w-full h-full object-cover object-center rounded-xl shadow-sm"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-xl">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Menu Item Content on right - bottom aligned */}
                      <div className="lg:w-1/2 xl:w-3/5 lg:flex lg:flex-col lg:justify-end">
                      
                      <div className="mb-2">
                        <h1 className="text-3xl md:text-4xl font-heading text-brand-navy">{menuItem.name}</h1>
                        {menuItem.calories && (
                          <div className="text-gray-600 text-sm mt-1">
                            <span>{menuItem.calories} calories</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Dietary Tags */}
                      {menuItem.dietary && menuItem.dietary.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {menuItem.dietary.map(diet => (
                            <span 
                              key={diet} 
                              className="inline-block bg-brand-cream text-brand-navy px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {diet.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {menuItem.plainDescription && (
                        <div className="prose prose-lg max-w-none text-gray-700 mb-6">
                          <p>{menuItem.plainDescription}</p>
                        </div>
                      )}
                      
                      {/* CTA Buttons - moved below description */}
                      <div className="flex flex-wrap gap-3 mb-6">
                        <Button 
                          variant="solid" 
                          color="navy"
                          onClick={() => {
                            trackOnlineOrderClick(menuItem?.name || 'Unknown', 'menu_drawer');
                            handleOpenLocationModal();
                          }}
                          icon={
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          }
                        >
                          Order Online
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          color="navy"
                          onClick={() => {
                            trackReservationClick(menuItem?.name || 'Unknown', 'menu_drawer');
                            handleOpenLocationModal();
                          }}
                          icon={
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          }
                        >
                          Join Waitlist
                        </Button>
                      </div>
                      
                      </div>
                    </div>
                    
                    {/* Availability Section - Full width below image and content */}
                    <div className="mt-8 bg-gray-50 pt-6 pb-6">
                      <div className="px-4 lg:px-8">
                        <h3 className="text-lg font-heading text-brand-navy mb-4">Available at these locations:</h3>
                        
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                          {menuItem.locations && menuItem.locations.length > 0 ? (
                            menuItem.locations.map(location => (
                              <Link 
                                key={location._id}
                                to={location.slug ? `/locations/${location.slug}` : `/locations`}
                                className="flex items-center p-4 bg-white rounded-md hover:shadow-md transition-all"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-brand-navy mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm font-medium text-brand-navy truncate">{location.name}</span>
                              </Link>
                            ))
                          ) : (
                            <div className="col-span-2 lg:col-span-3 p-4 bg-white rounded-md text-sm text-gray-600">
                              Available at all locations
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Related Items */}
                    {relatedItems && relatedItems.length > 0 && (
                      <div className="-mt-px -mb-4 bg-brand-navy py-6">
                        <div className="px-4 lg:px-8">
                          <h2 className="text-xl font-heading text-white mb-4">You May Also Like</h2>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {relatedItems.map((item) => (
                            <div 
                              key={item._id}
                              className="bg-white rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => {
                                // Get the current path to determine context
                                const currentPath = window.location.pathname;
                                onClose();
                                
                                setTimeout(() => {
                                  // If we're in a category page, stay there
                                  if (currentPath.split('/').length === 3 && currentPath.startsWith('/menu/')) {
                                    navigate(`/menu/${categorySlug}/${item.slug}`);
                                  } else {
                                    // Otherwise we're in the main menu page
                                    navigate(`/menu/${categorySlug}/${item.slug}`);
                                  }
                                }, 300); // Small delay to allow drawer to close
                              }}
                            >
                              <div className="h-32 overflow-hidden">
                                {item.imageUrl ? (
                                  <img 
                                    src={item.imageUrl} 
                                    alt={item.name} 
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div className="p-4">
                                <h3 className="font-bold text-brand-navy">{item.name}</h3>
                                {item.plainDescription && (
                                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">{item.plainDescription}</p>
                                )}
                              </div>
                            </div>
                          ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
          
          {/* Location Selection Modal */}
          <Suspense fallback={null}>
            <LocationSelectionModal
              isOpen={locationModalOpen}
              onClose={() => setLocationModalOpen(false)}
              locations={locations}
            />
          </Suspense>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
